import { useStyletron } from "baseui";
import { ButtonGroup, SIZE, SHAPE, MODE } from 'baseui/button-group';
import { Button } from 'baseui/button';
import { useEffect, useMemo, useState } from "react";
import { Block } from "baseui/block";
import { Select } from "baseui/select";
import { Bar } from "react-chartjs-2";
import { getDefaultOptions } from "./defaultOptions";
import { FormattedMessage, useIntl } from "react-intl";
import { getBuildingColor, getBuildingTypeColor } from "./utils";
import { Badge } from "baseui/badge";
import { addDays, addMonths, differenceInDays, endOfMonth, startOfMonth } from 'date-fns'
import { IBuildingCompare } from "./types";
import { ProgressBar } from "baseui/progress-bar";
import { useGetBuildings } from "../../../../hooks/useGetBuildings";
import { BuildingType, TP_Building } from "../../../Plants/Buildings/P_Building";
import { TGroupByItem, groupBy } from "../../../../utils/arrayUtils";
import { LoadingView } from "../../../../components";
import { DisplayMode, useGetBuildingsEnergy } from "../../../../hooks/useGetBuildingsEnergy";
import { useGetLanguage } from "../../../../hooks/useGetLanguage";
import TimeNavigation, { onTimeChangedArgs } from "../../../../features/Charts/TimeNavigation/TimeNavigation";
import { EmptyState } from "../../../../components/EmptyState";
import { defaultDataSet } from "../../../Utils/EnergyOverview/defaultOptions";
import { IBaseCounter } from "../../../Devices/BaseCounter";
import { CounterMedium } from "../../../Devices/CounterMedium";
import { BsCurrencyExchange } from "react-icons/bs";

const OperatorPanel = (props: IBuildingCompare) => {
    const [css, theme] = useStyletron();
    const [buildingTypes, setBuildingTypes] = useState<any>([]);

    const intl = useIntl();
    // get buildings
    const { items: buildings, count, error, isLoading, isSuccess } = useGetBuildings();
    const filteredBuildings = useMemo(() => {
        if (buildingTypes.length > 0)
            return buildings?.slice().filter((item: TP_Building) => buildingTypes.find((x) => x.id === item.buildingType))
        else
            return buildings?.slice();
    }, [buildings, buildingTypes]);

    const selectableBuildingTypes = useMemo(() => {
        return buildings ?
            groupBy(buildings, 'buildingType').map((item: TGroupByItem, index: number) => {
                return {
                    label: intl.formatMessage({ id: `BuildingType.${BuildingType[item.key]}`, defaultMessage: BuildingType[item.key] }),
                    id: item.key,
                    color: theme.colors.accent,
                    count: item.values.length,
                }
            }).sort((a, b) => a.label.localeCompare(b.label))
            : [];
    }, [buildings]);

    return (
        <Block height='100%' width={'100%'} display={'flex'} flexDirection='column' gridGap={theme.sizing.scale400}>
            <LoadingView isLoading={isLoading} error={error} isSucess={isSuccess}>
                <Block display={'flex'} flexDirection='row' gridGap={theme.sizing.scale400}>
                    <Select
                        value={buildingTypes}
                        multi
                        searchable={false}
                        size="compact"
                        placeholder="Filter by Building Type ..."
                        onChange={params => setBuildingTypes(params.value)}
                        options={selectableBuildingTypes}
                        getOptionLabel={getLabel}
                        getValueLabel={getLabel}
                    />
                </Block>
                {filteredBuildings && <Chart buildings={filteredBuildings}></Chart>}
            </LoadingView>
        </Block>);
}

export default OperatorPanel;


const Chart = (props: { buildings: TP_Building[] }) => {
    const [css, theme] = useStyletron();
    const [timeMode, setTimeMode] = useState(timeModes.month);
    const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.EUR);
    const [perM2, setPerM2] = useState<boolean>(true);
    const [start, setStart] = useState<Date>(addMonths(startOfMonth(new Date()), -12))
    const [end, setEnd] = useState<Date>(endOfMonth(new Date()))
    const [data, setData] = useState<any>({ datasets: [] });
    const { locale } = useGetLanguage();

    const handleFilter = (counter: IBaseCounter) => counter.counterMedium !== CounterMedium.WaterMeter;
    const { items: historicalData, count, average, isLoading, error, isSuccess, isFetching } = useGetBuildingsEnergy(displayMode, start, end, timeMode.summarize, perM2, handleFilter);


    //const unit = perM2 ? `${displayModeUnits[displayMode]}/m2` : displayModeUnits[displayMode];
    const myOptions = useMemo(() => getDefaultOptions(theme, locale, true, calcAverage, timeMode.scaleUnit, ""), [timeMode.scaleUnit, average]);
    function calcAverage() {
        return average;
    }

    useEffect(() => {
        if (historicalData) {
            const dt = historicalData.sort((a, b) => (b.building?.buildingType || 0) - (a.building?.buildingType || 0))
                .map((item) => {
                    return {
                        ...defaultDataSet,
                        //scaleID: 'y',
                        label: item.building?.description,
                        data: item.data.map((value) => { return { x: value.timeStamp, y: value.sumValue } }),
                        backgroundColor: getBuildingColor(theme, item.building?.buildingType, item.building?.description || ''),
                    }
                });
            setData({ datasets: dt });
        }
    }, [historicalData]);

    const hasCounters = count > 0;

    function goBack() {
        const daydiff = differenceInDays(end, start);
        setStart(addDays(start, daydiff * -1))
        setEnd(addDays(end, daydiff * -1))
    }
    function goForward() {
        const daydiff = differenceInDays(end, start);
        setStart(addDays(start, daydiff))
        setEnd(addDays(end, daydiff))
    }
    function goLive() {
        const daydiff = differenceInDays(end, start);
        setStart(addDays(new Date(), daydiff * -1))
        setEnd(new Date())
    }
    function onTimeChanged(args?: onTimeChangedArgs) {
        if (args) {
            setStart(args?.start)
            setEnd(args?.end)
        }
    }

    return (
        <>
            <Block display={'flex'} height='100%' width='100%' gridGap={theme.sizing.scale400} flexDirection='column'>
                <div className={css({ display: 'contents', width: '100%' })}>
                    <div className={css({ flex: '0 1 auto' })}>
                        <Block display={'flex'} justifyContent='space-between'>

                            <ButtonGroup
                                size={SIZE.mini}
                                shape={SHAPE.default}
                                mode='radio'
                            >
                                <Button isSelected={timeMode === timeModes.day} onClick={() => setTimeMode(timeModes.day)}>Tag</Button>
                                <Button isSelected={timeMode === timeModes.week} onClick={() => setTimeMode(timeModes.week)}>Woche</Button>
                                <Button isSelected={timeMode === timeModes.month} onClick={() => setTimeMode(timeModes.month)}>Monat</Button>
                                <Button isSelected={timeMode === timeModes.year} onClick={() => setTimeMode(timeModes.year)}>Jahr</Button>
                            </ButtonGroup>
                            <Block  display={'inline-flex'} gridGap={theme.sizing.scale100}>
                                <ButtonGroup
                                    size={SIZE.mini}
                                    shape={SHAPE.default}
                                    mode='radio'
                                    selected={displayMode}
                                    onClick={(event, index) => {
                                        setDisplayMode(index);
                                    }}
                                >
                                    <Button><BsCurrencyExchange /></Button>
                                    <Button>CO2</Button>
                                    <Button>kWh</Button>
                                </ButtonGroup>
                                <Button kind="secondary" size={SIZE.mini} shape={SHAPE.default} onClick={() => setPerM2(!perM2)} isSelected={perM2}><FormattedMessage id="perM2" defaultMessage={'per m²'}></FormattedMessage></Button>
                            </Block>
                        </Block>
                    </div>
                    <div className={css({ flex: '1 0 auto', opacity: isFetching ? 0.5 : 1.0 })}>
                        {isFetching && <ProgressBar size="small" infinite />}
                        <LoadingView isLoading={isLoading} error={error} isSucess={isSuccess}>
                            {!hasCounters ?
                                <EmptyState header="No Data" message="No data found" ></EmptyState>
                                :
                                <Bar data={data} datasetIdKey='id' options={myOptions}  ></Bar>
                            }
                        </LoadingView>
                    </div>
                    <div className={css({ flex: '0 1 auto', width: '100%' })}>
                        <TimeNavigation start={start} end={end} goBack={goBack} goForward={goForward} goLive={goLive} onTimeChanged={onTimeChanged}></TimeNavigation>
                    </div>
                </div>
            </Block>
        </>
    );
}


const getLabel = ({ option }: any) => {
    return (
        <>
            <Badge overrides={{
                Badge: {
                    style: ({ $theme }) => ({
                        backgroundColor: getBuildingTypeColor(option.id, $theme)
                    })
                }
            }} content={option.count} shape={SHAPE.pill}></Badge> {option.label}
        </>
    );
};

const timeModes: IDictionary = {
    'day': { summarize: "1d", scaleUnit: 'day' },
    'week': { summarize: "7d", scaleUnit: 'week' },
    'month': { summarize: "month", scaleUnit: 'month' },
    'year': { summarize: "year", scaleUnit: 'year' },
}

interface IDictionary {
    [index: string]: any;
}


const displayModeUnits: IDictionary = {
    [DisplayMode.EUR]: "€",
    [DisplayMode.CO2]: "kg",
    [DisplayMode.KWH]: "kWh",
}
